if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.sectionId = this.dataset.sectionId;
        this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));

        this.form.addEventListener('change', (e) => {
          if (e.target.name !== 'quantity') return;
          const qty = parseInt(e.target.value) || 1;
          const basePrice = parseInt(this.submitButton.dataset.basePrice) || 0;
          const baseCompare = parseInt(this.submitButton.dataset.baseCompare) || 0;
          this.setAtcPrice(basePrice * qty, baseCompare * qty);
        });
      }

      disconnectedCallback() {
        this.unsubVariant?.();
      }

      setAtcPrice(price, comparePrice) {
        const priceEl = this.submitButton.querySelector('.atc-price');
        const compareEl = this.submitButton.querySelector('.atc-compare');
        if (priceEl) priceEl.textContent = Shopify.formatMoney(price, theme.moneyFormat);
        if (compareEl) {
          if (comparePrice && comparePrice > price) {
            compareEl.textContent = Shopify.formatMoney(comparePrice, theme.moneyFormat);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (sectionId !== this.sectionId || !variant) return;
        this.submitButton.dataset.basePrice = variant.price;
        this.submitButton.dataset.baseCompare = variant.compare_at_price || 0;
        const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value) || 1;
        this.setAtcPrice(variant.price * qty, (variant.compare_at_price || 0) * qty);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Per-unit variant selectors in the quantity-breaks block need one cart
        // line per variant, which the form's single id/quantity pair cannot
        // express. null — the normal case, and every other <product-form> in the
        // theme — leaves everything below exactly as it was.
        const items =
          document.querySelector(`quantity-breaks[data-section="${this.sectionId}"]`)?.getCartItems?.() || null;
        const skipCart = this.form.dataset.skipCart === 'true';

        if (skipCart && !items) {
          const f = document.createElement('form');
          f.method = 'POST';
          f.action = '/cart/add';
          f.style.display = 'none';
          const formData = new FormData(this.form);
          formData.append('return_to', '/checkout');
          formData.forEach((val, key) => {
            const i = document.createElement('input');
            i.type = 'hidden'; i.name = key; i.value = val;
            f.appendChild(i);
          });
          document.body.appendChild(f);
          f.submit();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        let formData = null;

        if (items) {
          // Documented multi-line shape of /cart/add.js, same call that
          // component-bundle-offer.js already makes in this theme.
          const body = { items };
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(body);
        } else {
          delete config.headers['Content-Type'];
          formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        const eventVariantId = items ? items[0].id : formData.get('id');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: eventVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Buy now: the line(s) are in the cart, go straight to checkout.
            if (skipCart) {
              window.location = '/checkout';
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: eventVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
